<template>
  <div ref="container" class="relative w-full">
    <div
      class="flex w-full items-center gap-2 overflow-x-hidden overflow-y-visible px-5 py-3"
    >
      <div
        v-for="(file, index) in visibleFiles"
        :key="index"
        class="flex-1 min-w-25 max-w-fit"
      >
        <selected-file :file="file" @file:remove="removeFile(index)" />
      </div>
      <extra-files
        v-if="remainingCount"
        :remaining-files="remainingFiles"
        @file:remove="removeRemainingFile"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import { useFiles } from "@/stores/ai/files";

import ExtraFiles from "@/components/cmdK/chat/files/ExtraFiles.vue";
import SelectedFile from "@/components/cmdK/chat/files/SelectedFile.vue";

const aiFilesStore = useFiles();

const { removeFile } = aiFilesStore;
const { selectedFiles } = storeToRefs(aiFilesStore);

const container = ref<HTMLElement | null>(null);
const visibleCount = ref(selectedFiles.value.length);

const { width: containerWidth } = useElementSize(container);

const visibleFiles = computed(() =>
  selectedFiles.value.slice(0, visibleCount.value)
);
const remainingFiles = computed(() =>
  selectedFiles.value.slice(visibleCount.value)
);
const remainingCount = computed(() => remainingFiles.value.length);

const removeRemainingFile = (file: File) => {
  const index = selectedFiles.value.indexOf(file);
  if (index !== -1) {
    removeFile(index);
  }
};

const calculateVisible = () => {
  if (!containerWidth.value) {
    return;
  }

  const total = selectedFiles.value.length;

  if (total < 3) {
    visibleCount.value = total;
    return;
  }

  const padding = 40; // Container padding (p-5 = 20px left/right) -> 40px
  const gap = 8; //gap-2
  const availableWidth = containerWidth.value - padding;
  const plusBtnWidth = 40; // Approx min-width of +N button
  const maxItemWidthWhenShrinked = 180;

  const totalWidth = total * maxItemWidthWhenShrinked + (total - 1) * gap;

  const canFitAll = totalWidth <= availableWidth;

  if (canFitAll) {
    visibleCount.value = total;
    return;
  }

  const itemTotalWidth = maxItemWidthWhenShrinked + gap;
  const extraItemsButtonWidth = gap + plusBtnWidth;

  const maxItemsToFit = Math.floor(
    (availableWidth - extraItemsButtonWidth) / itemTotalWidth
  );

  visibleCount.value = Math.max(3, Math.min(maxItemsToFit, total - 1));
};

watch(
  [containerWidth, () => selectedFiles.value.length],
  () => {
    nextTick(calculateVisible);
  },
  { immediate: true }
);
</script>
